import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../../Styles/ann-tokens.css";
import "../../Styles/marketing-base.css";
import "../../Styles/pages/send-role.css";

/* ============================================================
   "Send Ann a role" — intake form (Claude Design import)
   Same editorial register as First Principles / Pricing.
   Submits via FormSubmit.co (no backend of our own) straight to
   teja@prosapiens.in, then swaps to a "Received" confirmation.
   Note: FormSubmit requires the recipient to click a one-time
   activation link the first time a submission lands.
   ============================================================ */

// AJAX endpoint — POSTing here (rather than to the plain-form
// endpoint) gets a JSON response back instead of a redirect, so
// the page can stay on the "Received" state instead of navigating
// away to FormSubmit's own confirmation page.
const FORM_ENDPOINT = "https://formsubmit.co/ajax/teja@prosapiens.in";

const COMES_BACK = [
  {
    title: "A RoleDNA",
    body:
      "The definition of success for this role, written down: what the person must " +
      "have produced by the end of their first months, the capabilities that " +
      "produce those outcomes, the critical few factors that separate success " +
      "from adequacy, and the failure patterns that make strong-looking " +
      "candidates the wrong hire here. It also states its own confidence, and " +
      "what it would need in order to be more certain.",
  },
  {
    title: "Five candidates, sourced",
    body:
      "Sourced against that definition and assessed against it — with the " +
      "evidence behind each assessment, the gaps named rather than glossed, " +
      "and the reasoning you can follow to the score.",
  },
  {
    title: "Access to the project",
    body:
      "Login credentials to the live project, so you can read the working " +
      "rather than take our word for it.",
  },
];

const NEXT_STEPS = [
  "You will get a confirmation email immediately, so you know the role arrived.",
  "Ann defines success for the role and sources against that definition. This takes 48 hours.",
  "Your credentials arrive by email. The RoleDNA and the five evaluated candidates are waiting in the project.",
  "We will call to walk you through it — what Ann found, what it could not verify, and where it is uncertain. Fifteen minutes. If the output is not useful, that call is where you tell us why, and that is the more useful conversation.",
];

export default function SendRole() {
  const [role, setRole] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [location, setLocation] = useState("");
  const [expFrom, setExpFrom] = useState("");
  const [expTo, setExpTo] = useState("");
  const [workMode, setWorkMode] = useState("");
  const [skills, setSkills] = useState("");
  const [hiringFor, setHiringFor] = useState("own");
  const [company, setCompany] = useState("");
  const [clientCompany, setClientCompany] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const canSubmit =
    (role.trim() !== "" || attachedFile !== null) &&
    email.trim() !== "" &&
    phone.trim() !== "";

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit || submitting) return;
    setSubmitError("");
    setSubmitting(true);

    const data = new FormData();
    data.append("_subject", `New role — ${roleTitle.trim() || company.trim() || "Send Ann a role"}`);
    data.append("_template", "table");
    data.append("_captcha", "false");
    if (email.trim()) data.append("_replyto", email.trim());

    data.append("Role title", roleTitle.trim() || "(not provided — Ann will infer it)");
    data.append(
      "Job description / outcomes",
      role.trim() || (attachedFile ? "(see attached file)" : "")
    );
    data.append("Location", location.trim());
    data.append(
      "Experience",
      expFrom.trim() || expTo.trim() ? `${expFrom.trim() || "?"}–${expTo.trim() || "?"} yrs` : ""
    );
    data.append("Work mode", workMode);
    data.append("Must-have skills", skills.trim());
    data.append("Hiring for", hiringFor === "client" ? "A client" : "My own company");
    data.append("Company", company.trim());
    if (hiringFor === "client") data.append("Client company", clientCompany.trim());
    data.append("Name", name.trim());
    data.append("Email", email.trim());
    data.append("Phone", phone.trim());
    data.append("Notes", notes.trim());
    if (attachedFile) data.append("attachment", attachedFile, attachedFile.name);

    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { Accept: "application/json" },
        body: data,
      });
      if (!res.ok) throw new Error("FormSubmit request failed");
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "auto" });
    } catch (err) {
      setSubmitError(
        "Something went wrong sending this. Please try again, or email teja@prosapiens.in directly."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="sar">
      {/* HERO + INTAKE */}
      <section className="hero wrap">
        <p className="kicker">The offer</p>
        <h1>Send Ann a role</h1>
        <p className="lede">
          One live role, free. Within 48 hours: a definition of what success in
          it looks like, five candidates sourced and evaluated against that
          definition, and access to the project so you can see the reasoning.
        </p>
      </section>

      <section className="card-section wrap">
        <div className="sar-card">
          {!submitted ? (
            <form onSubmit={handleSubmit}>
              {/* GROUP 1 */}
              <div className="group">
                <h2>The role</h2>
                <p className="hint">
                  Paste the job description if you have one. If you don&rsquo;t,
                  describe what this person must achieve &mdash; that is the more
                  useful input anyway.
                </p>
                <textarea
                  className="text-input"
                  rows={6}
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Paste the job description here — or write what this person must produce in their first six months, and what would make you certain you hired the right one."
                />
                <label className="attach">
                  <span className="plus" aria-hidden="true">
                    +
                  </span>
                  {attachedFile ? "Change attached file" : "Or attach a file — PDF, DOCX, TXT"}
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    onChange={(e) => setAttachedFile(e.target.files?.[0] || null)}
                  />
                </label>
                {attachedFile ? (
                  <p className="attach-name">Attached: {attachedFile.name}</p>
                ) : null}

                <div className="field">
                  <label htmlFor="sar-role-title">
                    Role title{" "}
                    <span className="opt">
                      &mdash; optional; Ann will infer it if you leave this blank
                    </span>
                  </label>
                  <input
                    id="sar-role-title"
                    type="text"
                    className="text-input"
                    value={roleTitle}
                    onChange={(e) => setRoleTitle(e.target.value)}
                  />
                </div>
              </div>

              <hr />

              {/* GROUP 2 */}
              <div className="group">
                <h2>Where Ann should search</h2>
                <p className="hint">
                  Job descriptions are usually clear about responsibilities and
                  vague about these. Ann needs them to search, so a few seconds
                  here saves a round of email.
                </p>

                <div className="grid-2">
                  <div>
                    <label htmlFor="sar-location">Location</label>
                    <input
                      id="sar-location"
                      type="text"
                      className="text-input"
                      placeholder="City, or the region the role can hire from"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                    />
                  </div>
                  <div>
                    <span className="field-label">Experience</span>
                    <div className="exp-range">
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        placeholder="from"
                        className="text-input"
                        value={expFrom}
                        onChange={(e) => setExpFrom(e.target.value)}
                      />
                      <span>to</span>
                      <input
                        type="number"
                        min="0"
                        inputMode="numeric"
                        placeholder="to"
                        className="text-input"
                        value={expTo}
                        onChange={(e) => setExpTo(e.target.value)}
                      />
                      <span className="unit">yrs</span>
                    </div>
                  </div>
                </div>

                <div className="field">
                  <span className="field-label">Work mode</span>
                  <div className="chips">
                    {["On-site", "Hybrid", "Remote"].map((m) => (
                      <button
                        key={m}
                        type="button"
                        className={`chip${workMode === m ? " is-on" : ""}`}
                        aria-pressed={workMode === m}
                        onClick={() => setWorkMode(m)}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="sar-skills">
                    Must-have skills <span className="opt">&mdash; optional</span>
                  </label>
                  <input
                    id="sar-skills"
                    type="text"
                    className="text-input"
                    placeholder="Only if the description doesn't already make these obvious"
                    value={skills}
                    onChange={(e) => setSkills(e.target.value)}
                  />
                </div>
              </div>

              <hr />

              {/* GROUP 3 */}
              <div className="group">
                <h2>Where to send it</h2>

                <div className="field" style={{ marginTop: 0, marginBottom: 24 }}>
                  <span className="field-label">I am hiring for</span>
                  <div className="chips">
                    <button
                      type="button"
                      className={`chip${hiringFor === "own" ? " is-on" : ""}`}
                      aria-pressed={hiringFor === "own"}
                      onClick={() => setHiringFor("own")}
                    >
                      My own company
                    </button>
                    <button
                      type="button"
                      className={`chip${hiringFor === "client" ? " is-on" : ""}`}
                      aria-pressed={hiringFor === "client"}
                      onClick={() => setHiringFor("client")}
                    >
                      A client
                    </button>
                  </div>
                </div>

                <div className="grid-2">
                  <div>
                    <label htmlFor="sar-company">Company</label>
                    <input
                      id="sar-company"
                      type="text"
                      className="text-input"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                    <p className="helper">
                      Ann factors in stage, size and context. It changes the
                      definition.
                    </p>
                  </div>

                  {hiringFor === "client" ? (
                    <div>
                      <label htmlFor="sar-client-company">
                        Client company <span className="opt">&mdash; optional</span>
                      </label>
                      <input
                        id="sar-client-company"
                        type="text"
                        className="text-input"
                        value={clientCompany}
                        onChange={(e) => setClientCompany(e.target.value)}
                      />
                      <p className="helper">
                        Not required, but it sharpens both the definition and
                        the search.
                      </p>
                    </div>
                  ) : null}

                  <div>
                    <label htmlFor="sar-name">Your name</label>
                    <input
                      id="sar-name"
                      type="text"
                      className="text-input"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div>
                    <label htmlFor="sar-email">Work email</label>
                    <input
                      id="sar-email"
                      type="email"
                      className="text-input"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                    <p className="helper">
                      Where your credentials and the RoleDNA will arrive.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="sar-phone">Phone</label>
                    <input
                      id="sar-phone"
                      type="tel"
                      inputMode="tel"
                      className="text-input"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                    <p className="helper">
                      So we can walk you through the project once it is ready.
                    </p>
                  </div>
                </div>

                <div className="field">
                  <label htmlFor="sar-notes">
                    Anything Ann should know{" "}
                    <span className="opt">&mdash; optional</span>
                  </label>
                  <input
                    id="sar-notes"
                    type="text"
                    className="text-input"
                    placeholder="Constraints, why the role opened, what went wrong last time."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                  />
                </div>
              </div>

              <div className="submit-row">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={!canSubmit || submitting}
                >
                  {submitting ? "Sending…" : "Send the role"}
                </button>
                <p className="submit-fineprint">
                  Free. One role. Your role is not shared, published, or used to
                  train anything.
                </p>
                {submitError ? <p className="submit-error">{submitError}</p> : null}
              </div>
            </form>
          ) : (
            <div className="received">
              <p className="kicker">Received</p>
              <h2>The role is with Ann.</h2>
              <p className="copy">
                A confirmation is on its way to{" "}
                <span className="email">{email.trim() || "your inbox"}</span>.
                Within 48 hours you will receive credentials to the project,
                where the RoleDNA and five evaluated candidates will be
                waiting. We will call shortly after to walk you through it.
              </p>
              <p className="copy">
                When you read the RoleDNA, read it against what you already
                know about this role. If it tells you something you had not
                written down, that is the point. If it misses something, say
                so on the call &mdash; that is equally the point.
              </p>
              <p className="note">Nothing else will be sent to you.</p>
              <Link to="/first-principles" className="next-link">
                Read the thinking behind this <span className="arr">&rarr;</span>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* WHAT COMES BACK */}
      <section className="info-section wrap">
        <p className="kicker" style={{ letterSpacing: ".18em" }}>
          What comes back
        </p>
        <div className="comes-back">
          {COMES_BACK.map((item) => (
            <div key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
            </div>
          ))}
        </div>
        <p className="comes-back-foot">
          The RoleDNA is the document the essay describes. Read it against a
          role you know, and judge it on whether it is right.{" "}
          <Link to="/first-principles">Read the argument &rarr;</Link>
        </p>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="info-section wrap">
        <p className="kicker" style={{ letterSpacing: ".18em" }}>
          What happens next
        </p>
        <div className="steps">
          {NEXT_STEPS.map((body, i) => (
            <div className="step" key={body}>
              <span className="n">{String(i + 1).padStart(2, "0")}</span>
              <p>{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STAFFING QUIET LINE */}
      <section className="quiet-line wrap">
        <p>
          Running a search firm? The same intake works &mdash; Ann is priced
          and shaped differently for firms.{" "}
          <Link to="/for/staffing-firms">Ann for recruitment firms &rarr;</Link>
        </p>
      </section>
    </div>
  );
}
